import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, ArrowLeft, Check, MapPin, Home } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MemberLayout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { ChevronsUpDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const FEATURED_NAMES = ["Väla-HBG", "Malmö C", "Nova-Lund"];

export default function RegisterActivity() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [typeId, setTypeId] = useState(null);
  const [locId, setLocId] = useState(null);
  const [count, setCount] = useState(0);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [openLoc, setOpenLoc] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [total, setTotal] = useState(null);

  useEffect(() => {
    api.get("/activity-types").then(r => setTypes(r.data));
    api.get("/locations").then(r => setLocations(r.data));
    api.get("/organizations").then(r => setOrgs(r.data));
  }, []);

  const DEFAULT_ORDER = ["Valbod", "Kampanjaktivitet", "Arbetsplatsbesök"];
  const defaultTypes = [...types.filter(t => t.category !== "kommunövergripande")]
    .sort((a, b) => {
      const ai = DEFAULT_ORDER.indexOf(a.name);
      const bi = DEFAULT_ORDER.indexOf(b.name);
      if (ai === -1 && bi === -1) return a.name.localeCompare(b.name, "sv");
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  const kommunTypes = types.filter(t => t.category === "kommunövergripande");

  const myOrg = orgs.find(o => o.id === user?.organization_id);
  const featured = locations.filter(l => FEATURED_NAMES.includes(l.name));
  // "Min kommun" = the kommun-type location matching user's org name
  const myKommunLoc = user?.organization_id
    ? locations.find(l => l.location_type === "kommun" && l.organization_id === user.organization_id)
    : null;
  const others = locations.filter(l =>
    !FEATURED_NAMES.includes(l.name) && l.id !== myKommunLoc?.id
  );
  const selectedLoc = locations.find(l => l.id === locId);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/registrations", {
        activity_type_id: typeId, location_id: locId,
        conversation_count: count, activity_date: date,
      });
      const stats = await api.get("/stats/me");
      setTotal(stats.data.total_conversations);
      setDone({ count, locName: locations.find(l => l.id === locId)?.name });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunde inte registrera");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <MemberLayout>
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center mb-4">
            <Check className="text-emerald-600" size={32}/>
          </div>
          <h2 className="font-heading text-3xl font-extrabold text-slate-900" data-testid="reg-success">Aktivitet registrerad!</h2>
          <p className="text-slate-600 mt-2">Du registrerade <span className="font-bold">{done.count}</span> samtal på <span className="font-bold">{done.locName}</span>.</p>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-6 inline-block">
            <div className="text-xs uppercase tracking-widest text-slate-500">Din totala summa</div>
            <div className="font-heading text-4xl font-extrabold text-slate-900 number-tabular mt-1" data-testid="reg-success-total">{total}</div>
            <div className="text-xs text-slate-500 mt-1">samtal</div>
          </div>
          <div className="mt-8 flex flex-col gap-3">
            <Button data-testid="reg-another" onClick={() => { setDone(null); setStep(1); setTypeId(null); setLocId(null); setCount(0); }}
              className="h-12 bg-[#E1251B] hover:bg-[#C81D14] text-white font-semibold rounded-full">Registrera en till</Button>
            <Button data-testid="reg-back-home" variant="outline" onClick={() => nav("/hem")} className="h-12 rounded-full">Tillbaka till hem</Button>
          </div>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout>
      <div className="flex items-center gap-2 mb-2 text-xs text-slate-500 font-semibold uppercase tracking-widest">
        <span>Steg {step} av 3</span>
        <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-[#E1251B] transition-all" style={{ width: `${(step/3)*100}%` }}/>
        </div>
      </div>

      {step === 1 && (
        <div>
          <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-6">Vilken typ av samtalsaktivitet?</h2>
          <div className="space-y-2">
            {defaultTypes.map(t => {
              const hint = t.name === "Kampanjaktivitet" ? "Exempelvis flygbladsutdelning vid stationer och butiker" : null;
              return (
                <button key={t.id} data-testid={`type-${t.name}`}
                  onClick={() => { setTypeId(t.id); setLocId(null); setStep(2); }}
                  className={cn("w-full text-left px-5 py-4 rounded-xl border-2 font-medium transition-colors",
                    typeId === t.id ? "border-[#E1251B] bg-red-50" : "border-slate-200 bg-white hover:border-slate-300")}>
                  <div>{t.name}</div>
                  {hint && <div className="text-xs text-slate-500 font-normal mt-0.5">{hint}</div>}
                </button>
              );
            })}
          </div>

          {kommunTypes.length > 0 && (
            <>
              <div className="mt-8 mb-3 flex items-center gap-3">
                <div className="h-px flex-1 bg-slate-200"/>
                <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">Kommunövergripande aktiviteter</div>
                <div className="h-px flex-1 bg-slate-200"/>
              </div>
              <div className="space-y-2">
                {kommunTypes.map(t => (
                  <button key={t.id} data-testid={`type-${t.name}`}
                    onClick={() => {
                      setTypeId(t.id);
                      if (t.linked_location_id) {
                        setLocId(t.linked_location_id);
                        setStep(3);
                      } else {
                        setStep(2);
                      }
                    }}
                    className={cn("w-full px-5 py-4 rounded-xl border-2 text-left transition-all",
                      typeId === t.id ? "border-[#E1251B] bg-red-50" : "border-slate-900 bg-slate-900 text-white hover:bg-slate-800")}>
                    <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1.5"><MapPin size={12}/> Nyckelplats</div>
                    <div className="font-heading text-xl font-extrabold mt-1">{t.name.toUpperCase()}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {step === 2 && (
        <div>
          <button onClick={() => setStep(1)} data-testid="back-step-1" className="text-xs text-slate-500 flex items-center gap-1 mb-3"><ArrowLeft size={14}/> Tillbaka</button>
          <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-6">Var ägde aktiviteten rum?</h2>

          <div className="grid grid-cols-1 gap-3 mb-6">
            {myKommunLoc && (
              <button data-testid="loc-mykommun" onClick={() => { setLocId(myKommunLoc.id); setStep(3); }}
                className={cn("px-6 py-5 rounded-xl border-2 text-left transition-all",
                  locId === myKommunLoc.id ? "border-[#E1251B] bg-red-50" : "border-slate-900 bg-slate-900 text-white hover:bg-slate-800")}>
                <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1.5"><Home size={12}/> Min kommun</div>
                <div className="font-heading text-2xl font-extrabold mt-1">{myOrg?.name || myKommunLoc.name}</div>
              </button>
            )}
            {featured.map(l => (
              <button key={l.id} data-testid={`loc-${l.name}`}
                onClick={() => { setLocId(l.id); setStep(3); }}
                className={cn("px-6 py-5 rounded-xl border-2 text-left transition-all",
                  locId === l.id ? "border-[#E1251B] bg-red-50" : "border-slate-900 bg-slate-900 text-white hover:bg-slate-800")}>
                <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1.5"><MapPin size={12}/> Nyckelplats</div>
                <div className="font-heading text-2xl font-extrabold mt-1">{l.name}</div>
              </button>
            ))}
          </div>

          {others.length > 0 && (<>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Annan plats</Label>
            <Popover open={openLoc} onOpenChange={setOpenLoc}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" data-testid="loc-other-trigger"
                  className={cn("w-full justify-between h-12 mt-2 font-normal", !locId && "text-slate-400")}>
                  {selectedLoc && !FEATURED_NAMES.includes(selectedLoc.name) && selectedLoc.id !== myKommunLoc?.id ? selectedLoc.name : "Välj annan plats…"}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50"/>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Sök plats…" data-testid="loc-search"/>
                  <CommandList>
                    <CommandEmpty>Ingen träff.</CommandEmpty>
                    <CommandGroup>
                      {others.map(l => (
                        <CommandItem key={l.id} value={l.name} data-testid={`loc-item-${l.name}`}
                          onSelect={() => { setLocId(l.id); setOpenLoc(false); setStep(3); }}>{l.name}</CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </>)}
        </div>
      )}

      {step === 3 && (
        <div>
          <button onClick={() => setStep(2)} data-testid="back-step-2" className="text-xs text-slate-500 flex items-center gap-1 mb-3"><ArrowLeft size={14}/> Tillbaka</button>
          <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-2">Hur många samtal?</h2>
          <p className="text-sm text-slate-500 mb-6">Tryck på + eller − för att räkna. Du kan även skriva direkt.</p>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 flex items-center justify-between">
            <Button variant="outline" data-testid="count-minus" onClick={() => setCount(c => Math.max(0, c - 1))}
              className="h-16 w-16 rounded-full text-2xl"><Minus size={28}/></Button>
            <Input data-testid="count-input" type="number" min={0} max={10000} value={count}
              onChange={e => setCount(Math.max(0, Math.min(10000, parseInt(e.target.value || "0"))))}
              className="text-center font-heading text-5xl font-extrabold border-0 bg-transparent number-tabular h-20 focus-visible:ring-0"/>
            <Button variant="outline" data-testid="count-plus" onClick={() => setCount(c => Math.min(10000, c + 1))}
              className="h-16 w-16 rounded-full text-2xl"><Plus size={28}/></Button>
          </div>
          <div className="grid grid-cols-4 gap-2 mt-3">
            {[5, 10, 25, 50].map(n => (
              <Button key={n} variant="outline" data-testid={`quick-${n}`} onClick={() => setCount(c => c + n)}>+{n}</Button>
            ))}
          </div>
          <div className="mt-6 space-y-2">
            <Label>Datum</Label>
            <Input data-testid="reg-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="h-12"/>
          </div>
          <Button data-testid="reg-submit" onClick={submit} disabled={submitting || !typeId || !locId}
            className="w-full h-14 mt-6 bg-[#E1251B] hover:bg-[#C81D14] text-white text-lg font-bold rounded-full">
            {submitting ? "Registrerar…" : "Registrera"}
          </Button>
        </div>
      )}
    </MemberLayout>
  );
}
